import { useGameStore } from '@/store/gameStore'
import { WEEKLY_GOALS, MILESTONE_GOALS, type Goal } from '@/config/sponsorshipConfig'

type GameState = ReturnType<typeof useGameStore.getState>

export interface GoalStats {
  tournamentsHosted: number
  customersServed: number
}

/** Real progress for a goal based on current game state. */
export function getGoalProgress(goal: Goal, state: GameState, stats: GoalStats): number {
  switch (goal.type) {
    case 'courts': return state.courts.length
    case 'reputation': return state.reputation
    case 'staff': return state.staff.length
    case 'tournaments': return stats.tournamentsHosted
    case 'members': return stats.customersServed
    case 'money': {
      // Weekly income: sum income transactions from the last 7 days
      const weekStart = Math.max(1, state.day - 7)
      return state.transactions
        .filter(t => t.type === 'income' && t.day >= weekStart)
        .reduce((sum, t) => sum + t.amount, 0)
    }
    default: return 0
  }
}

/** Goals completed but not yet claimed, in the same order GoalsTab renders them. */
export function getClaimableGoals(state: GameState, stats: GoalStats, claimedGoals: string[]): Goal[] {
  return [...WEEKLY_GOALS, ...MILESTONE_GOALS].filter(g => {
    const progress = getGoalProgress(g, state, stats)
    return progress >= g.target && !claimedGoals.includes(g.id)
  })
}

/** Count of goals completed but not yet claimed — i.e. rewards waiting to be collected. */
export function countClaimableGoals(state: GameState, stats: GoalStats, claimedGoals: string[]): number {
  return getClaimableGoals(state, stats, claimedGoals).length
}

/** First claimable goal (render order), for deep-linking straight to it. */
export function getFirstClaimableGoalId(state: GameState, stats: GoalStats, claimedGoals: string[]): string | null {
  return getClaimableGoals(state, stats, claimedGoals)[0]?.id ?? null
}
